import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "@styles/pages/Login/Clubs/ClubsLogin.scss";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  loginAccess,
  clubsLoginOTP,
  sendOTPCode,
  verificationOTP,
} from "@api/authenticationApi";
import LoadingButton from "@mui/lab/LoadingButton";
import { cryptoPassword } from "@utils/cryptoPassword";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLoginHandler } from "@utils/auth";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { toast } from "react-toastify";
import { useSearchParams, useLocation } from "react-router-dom";
import { setFormStep } from "@store/ui/formStepSlice";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { gaEvent } from "@utils/gaEvent";

const ClubsLogin = () => {
  const { t, i18n } = useTranslation();
  const currentStep = useSelector((state) => state.formStep.clubSLogin);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginHandler = useLoginHandler();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPField, setShowOTPField] = useState(false);
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [timer, setTimer] = useState(null);
  const [searchParams] = useSearchParams();
  const after_redirect_url = localStorage.getItem("redirect_url");

  const clubsLoginOTPRequest = async () => {
    const finalData = {
      email,
      password: cryptoPassword(password),
    };
    setIsLoading(true);
    try {
      const res = await clubsLoginOTP(finalData);
      const data = res.data.Data;
      const statusCode = res.data.StatusCode;

      switch (statusCode) {
        case 200:
          localStorage.setItem("sua_id", data.sua_id);
          setFirstName(data.first_name);
          postOTPCode();
          break;
        case 302:
          setIsLoading(false);
          toast.error(t("status_message:status_code_302"));
          break;
        case 303:
          setIsLoading(false);
          toast.error(t("status_message:status_code_303"));
          break;
        case 309:
          setIsLoading(false);
          toast.error(t("status_message:status_code_309"));
          break;
        case 310:
          setIsLoading(false);
          toast.error(t("status_message:status_code_310"));
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {

    }
  };

  const postOTPCode = async () => {
    setIsLoading(true);
    try {
      const res = await sendOTPCode();
      const statusCode = res.data.StatusCode;

      switch (statusCode) {
        case 200:
          setShowOTPField(true);
          startOTPTimer();
          dispatch(setFormStep({ formKey: "clubSLogin", step: 1 }));
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const startOTPTimer = () => {
    if (countdown > 0) return;

    // ✅ 開始 countdown
    setCountdown(180);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTimer(interval);
  };

  // ✅ 離開頁面時清 timer
  useEffect(() => {
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timer]);

  const verifyOTP = async () => {
    setIsLoading(true);
    try {
      const res = await verificationOTP({ code: otp });
      const statusCode = res.data.StatusCode;
      switch (statusCode) {
        case 200:
          clubsLoginRequest();
          break;
        case 500:
          toast.error(t("status_message:status_code_500"));
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  const clubsLoginRequest = async () => {
    const finalData = {
      email,
      password: cryptoPassword(password),
    };

    try {
      const res = await loginAccess(finalData);
      const data = res.data.Data;
      console.log(data);
      if (data) {
        loginHandler(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      navigate(after_redirect_url || "/");
    }
  };

  const handleSubmit = () => {
    verifyOTP();
  };

  return (
    <Box className="clubs-login-container">
      <Box className="description-container">
        {currentStep === 0 && (
          <>
            <Typography className="login-register-title">
              <Trans i18nKey="login:login_unlink_sa_title" />
            </Typography>{" "}
            <Typography className="login-register-title-2">
              <Trans i18nKey="login:login_unlink_sa_title_2" />
            </Typography>
            <Typography className="description">
              <Trans i18nKey="login:login_description" />
            </Typography>
          </>
        )}
        {currentStep === 1 && (
          <>
            <Box className="first-name-container">
              {firstName}

              <Trans i18nKey="login:login_otp_welcome" />
            </Box>
            <Typography className="description">
              <Trans i18nKey="login:login_otp_description" />
            </Typography>
            <Box className="email-container">
              <EmailOutlinedIcon className="login-otp-icon" />
              <Typography className="login-email">{email}</Typography>
            </Box>
          </>
        )}
      </Box>

      {!showOTPField && (
        <>
          <Box className="field-container">
            <Typography>
              <Trans i18nKey="login:email" />
            </Typography>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Box>
          <Box className="field-container">
            <Typography>
              <Trans i18nKey="login:password" />
            </Typography>
            <TextField
              fullWidth
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Box>
        </>
      )}
      {currentStep == 0 && (
        <Typography
          className="forget_password"
          onClick={() => {
            navigate("/forget_password");
          }}
        >
          <Trans i18nKey="login:forget_password" />
        </Typography>
      )}

      {showOTPField && (
        <Box className="field-contianer">
          <Typography>
            <Trans i18nKey="login:otp" />
          </Typography>
          <TextField
            fullWidth
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={postOTPCode}
                    disabled={countdown > 0 || isLoading}
                  >
                    {countdown > 0 ? (
                      <Typography fontSize={14} color="text.secondary">
                        <Trans i18nKey="login:resend" /> ({countdown})
                      </Typography>
                    ) : (
                      <AutorenewIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
      {showOTPField ? (
        <LoadingButton
          className="loading-button"
          disabled={isLoading}
          loading={isLoading}
          fullWidth
          onClick={() => {
            handleSubmit();
          }}
        >
          <Trans i18nKey="login:next" />
        </LoadingButton>
      ) : (
        <LoadingButton
          className="loading-button"
          disabled={isLoading}
          loading={isLoading}
          fullWidth
          onClick={() => {
            gaEvent("clubs_signup", "sign_up_page", "login_next");
            clubsLoginOTPRequest();
          }}
        >
          <Trans i18nKey="login:next" />
        </LoadingButton>
      )}
    </Box>
  );
};

export default ClubsLogin;
